import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

const CONVERSATION_KEY = 'k8sConsole.noraConversation';
const WIDTH_KEY = 'k8sConsole.noraDockWidth';
const MIN_WIDTH = 380;
const MAX_WIDTH = 900;

export interface NoraDockContext {
  cluster?: string;
  namespace?: string;
  kind?: string;
  name?: string;
  entityRef?: string;
}

export interface NoraResourceSelection {
  cluster: string;
  namespace?: string;
  kind: string;
  name: string;
}

function conversationId(): string {
  const stored = localStorage.getItem(CONVERSATION_KEY);
  if (
    stored &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      stored,
    )
  ) {
    return stored;
  }
  const created = uuidv4();
  localStorage.setItem(CONVERSATION_KEY, created);
  return created;
}

function initialWidth(): number {
  const stored = Number(localStorage.getItem(WIDTH_KEY));
  return Number.isFinite(stored)
    ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, stored))
    : 520;
}

export function NoraDock({
  context,
  onClose,
  onResourceSelect,
}: {
  context: NoraDockContext;
  onClose: () => void;
  onResourceSelect: (resource: NoraResourceSelection) => void;
}) {
  const compact = useMediaQuery('(max-width:1100px)');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const draggingRef = useRef(false);
  const [width, setWidth] = useState(initialWidth);
  const id = useMemo(conversationId, []);
  const canonicalParams = useMemo(() => {
    const params = new URLSearchParams({ conversation: id });
    for (const [key, value] of Object.entries(context)) {
      if (value) params.set(key, value);
    }
    return params;
  }, [context, id]);
  const embeddedUrl = `/nora?conversation=${encodeURIComponent(id)}&embedded=1`;

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'nora.context', context },
      window.location.origin,
    );
  }, [context]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== iframeRef.current?.contentWindow ||
        event.data?.type !== 'nora.resource.select'
      ) {
        return;
      }
      const resource = event.data.resource as Partial<NoraResourceSelection>;
      if (
        typeof resource.cluster === 'string' &&
        typeof resource.kind === 'string' &&
        typeof resource.name === 'string' &&
        (resource.namespace === undefined ||
          typeof resource.namespace === 'string')
      ) {
        onResourceSelect(resource as NoraResourceSelection);
      }
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [onResourceSelect]);

  useEffect(() => {
    const move = (event: PointerEvent) => {
      if (!draggingRef.current) return;
      const next = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, window.innerWidth - event.clientX),
      );
      setWidth(next);
    };
    const stop = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem(WIDTH_KEY, String(width));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
  }, [width]);

  return (
    <Box
      component="aside"
      aria-label="Nora assistant"
      sx={{
        position: 'fixed',
        zIndex: theme => theme.zIndex.drawer + 2,
        top: compact ? 0 : 64,
        right: 0,
        bottom: 0,
        width: compact ? '100vw' : width,
        bgcolor: '#0b1020',
        borderLeft: '1px solid #4f5255',
        boxShadow: '-12px 0 32px rgba(0,0,0,.36)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {!compact && (
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize Nora"
          onPointerDown={event => {
            event.preventDefault();
            draggingRef.current = true;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
          }}
          sx={{
            position: 'absolute',
            zIndex: 2,
            inset: '0 auto 0 -5px',
            width: 10,
            cursor: 'col-resize',
            '&:hover': { bgcolor: '#8bc1f755' },
          }}
        />
      )}
      <Box
        sx={{
          minHeight: 42,
          px: 1.5,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #30384e',
          bgcolor: '#11182a',
        }}
      >
        <Typography sx={{ flex: 1, color: '#f0f0f0', fontWeight: 700 }}>
          Nora · Kubernetes
        </Typography>
        <Tooltip title="Open full screen">
          <IconButton
            size="small"
            component="a"
            href={`/nora?${canonicalParams}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: '#c7c7c7' }}
          >
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close Nora">
          <IconButton size="small" onClick={onClose} sx={{ color: '#c7c7c7' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="iframe"
        ref={iframeRef}
        src={embeddedUrl}
        title="Nora assistant"
        onLoad={() =>
          iframeRef.current?.contentWindow?.postMessage(
            { type: 'nora.context', context },
            window.location.origin,
          )
        }
        sx={{ flex: 1, width: '100%', border: 0 }}
      />
    </Box>
  );
}
